import {
  GAME_CATEGORY,
  GAME_CATEGORY_ORDER,
  LEADERBOARD_ALL_GAMES,
  LEADERBOARD_PERIODS,
  isHiddenGame,
  type GameActivation,
  type GameId,
  type LeaderboardPeriod,
  type LeaderboardResponse,
  type LeaderboardRow,
} from "@bing/shared";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from "react";
import { GAME_ART_BLUR, GAME_ART_BLUR_WEBP } from "../assets/game-art-blur";
import "../styles/ladders-art.css";
import { Avatar } from "../components/Avatar";
import { GameMark } from "../components/GameMark";
import { gameMeta } from "../components/GameStats";
import { displayName } from "../components/profile-ui";
import { ProfileTap } from "../components/social-ui";
import { faDigits } from "../i18n/digits";
import { fa } from "../i18n/fa";
import { onThemeChange, type ResolvedTheme } from "../settings/prefs";
import { useSession } from "../store/context";

/**
 * «برترین‌ها» — the wins board.
 *
 * ── GOLD MEANS FIRST, OR SELECTED. NOTHING ELSE ───────────────────────────────────────────────
 * Gold at full strength appears exactly twice: the winner's plinth, and the selected period /
 * game chip (`secondary-container`, the same fill the bottom nav's active tab uses). It appears
 * once more at reduced strength — the viewer's own win count in the pinned row — and nowhere
 * else. It used to be on every list row's win count as well, which put the screen's one accent on
 * forty rows at once: with everything gold, nothing was. Second place is silver, third bronze, and
 * the list and filters sit back in the ordinary surface ladder so the gold has something to stand
 * out against. Nothing here uses `primary`: the lobby's mint green would read as a third,
 * unrelated highlight beside the gold.
 *
 * ── FIRST PLACE IS THE ONLY THING THAT LOOKS WON ──────────────────────────────────────────────
 * It has the tallest block (2× the third's), the largest photo, the only halo, and the only
 * saturated fill. Depth is the app's existing craft, borrowed from the game tiles and the
 * end-of-match card rather than invented here: a gradient face, a bevelled top edge, a real
 * `--card-shadow` on the ground, and one `--trick-glow` radial behind the centre column. The
 * three metals and the contrast constraint on each gradient live in `theme.css`.
 *
 * ── THE PAGE SCROLLS AS ONE ───────────────────────────────────────────────────────────────────
 * No nested scroll region — the filter grid included: it shows every game, at its own height (see
 * {@link GameFilterGrid}). Only the pinned row is fixed. Measured 2026-09-10: a fixed header plus
 * a scrolling list left 95px of list — ONE row — at 320×568, because the podium alone costs
 * ~170px. Letting the podium scroll away spends that budget once instead of on every frame.
 *
 * ── WHEN IT READS ─────────────────────────────────────────────────────────────────────────────
 * On open, and on returning to the tab. NEVER on a timer — the api caches the board for 5s and
 * bypasses that cache for anyone whose own last match ended after it was built.
 */

const PERIOD_LABEL: Record<LeaderboardPeriod, string> = {
  week: fa.leaderboard.week,
  month: fa.leaderboard.month,
  all: fa.leaderboard.all,
};

/** Chips in the filter's FIRST row: «همه» and the card games beside it — see {@link filterRows}. */
const FIRST_ROW_CHIPS = 4;
/** Chips in every row after the first. */
const FILTER_COLUMNS = 3;
/** The grid's tracks: twelve divide by every row's chip count, so each row's chips share it evenly. */
const FILTER_TRACKS = 12;

/**
 * The games the filter offers, in the LOBBY's order — derived, never a second list.
 *
 * The lobby renders the store's `lobbyGames` (the server's `/games`, sorted by `order`, the index
 * into `LOBBY_GAME_ORDER`) one category per tab, card tab first (`GAME_CATEGORY_ORDER`). This reads
 * the SAME three things in the same way, so «برترین‌ها» lists the card games first and then the
 * board games, each in its tab's order (card-86 round 2, the operator: «Order them like the lobby,
 * card games together first, then board games»). Grouping by category rather than trusting `order`
 * alone is what keeps the two agreeing should the order ever interleave the categories.
 *
 * Only ACTIVE games: a COMING_SOON one has no public table to rank, and appears on its own the day
 * it is switched on. A hidden game (`isHiddenGame` — بلوف) never, whatever its status: every
 * player-facing list asks, and this one does not rely on the store having asked first.
 */
export function leaderboardFilterGames(lobbyGames: readonly GameActivation[]): GameId[] {
  const shown = lobbyGames
    .filter((g) => g.status === "ACTIVE" && !isHiddenGame(g.game))
    .sort((a, b) => a.order - b.order);
  return GAME_CATEGORY_ORDER.flatMap((category) =>
    shown.filter((g) => GAME_CATEGORY[g.game] === category).map((g) => g.game),
  );
}

/**
 * The filter's rows, top to bottom, from the games in the lobby's order ({@link
 * leaderboardFilterGames}) — derived from the categories, never from names.
 *
 * The operator (card-86 round 2): «all 9 games in exactly 3 rows, never 4. The first row holds the
 * four card games side by side … the ones with short names. The five board games share the other
 * two rows, in the lobby's order» — «همه + 3 of the card games in first row, then other; first in
 * second row is another card game, then other games». So the first row is «همه» and the card
 * games that lead the list, until it holds {@link FIRST_ROW_CHIPS}; every game after that flows
 * {@link FILTER_COLUMNS} a row, in order. Nine games are exactly 4 · 3 · 3.
 */
export function filterRows(games: readonly GameId[]): (GameId | typeof LEADERBOARD_ALL_GAMES)[][] {
  let lead = 0;
  while (
    lead < games.length &&
    lead < FIRST_ROW_CHIPS - 1 &&
    GAME_CATEGORY[games[lead] as GameId] === "card"
  )
    lead += 1;
  const rows: (GameId | typeof LEADERBOARD_ALL_GAMES)[][] = [
    [LEADERBOARD_ALL_GAMES, ...games.slice(0, lead)],
  ];
  // A game past what two rows of three hold rides the second row (card-60, D24: a tenth game, 4 · 4 · 3:
  // «exactly 3 rows, never 4»). Nine or fewer flow as they always did.
  let wider = Math.max(0, games.length - lead - 2 * FILTER_COLUMNS);
  for (let i = lead; i < games.length;) {
    const size = FILTER_COLUMNS + (wider > 0 ? 1 : 0);
    wider = Math.max(0, wider - 1);
    rows.push(games.slice(i, i + size));
    i += size;
  }
  return rows;
}

/**
 * A game's Persian name and its mark, through the SAME accessor the match history and the per-game
 * breakdown use.
 *
 * ⚠️ **THERE IS ONE MAP AND IT LIVES IN `config.ts`.** This screen briefly carried its own copy of
 * the names AND a second map of marks with two overrides in it, because تخته نرد and منچ shared a
 * die — and a per-screen override is how one game comes to look like two different games depending
 * on which screen you are on. The collision is fixed at the SOURCE now, so every surface agrees and
 * there is nothing here to keep in step. `gameMeta` is `GameStats`'s own export.
 */

/**
 * ── WHY THE PINNED ROW WAITS FOR THE SCREEN TO STOP MOVING ────────────────────────────────────
 *
 * ⚠️ **A `position: fixed` BOX THAT MOUNTS INSIDE A TRANSFORM-ANIMATED ANCESTOR IS LAID OUT
 * AGAINST THAT ANCESTOR, NOT AGAINST THE VIEWPORT.** `ViewFade` animates an 8px rise on the app's
 * ONE scroll container — which is this screen's parent — and a transform-animated element is a
 * containing block for fixed descendants. `PinnedRow` is fixed. So for the 220ms the entrance
 * runs, `bottom: 63px` is measured from the SCROLLER's bottom edge, which is already the top of
 * the bottom nav, and the row sits a whole nav-height too high; when the class is dropped it
 * SNAPS down.
 *
 * ⚠️ **IT ONLY HAPPENS FROM THE SECOND VISIT ON, WHICH IS WHY IT SURVIVED THE FIRST FIX.** On the
 * first press the board has to be fetched, so the row mounts ~450ms in — after the entrance, and
 * against the viewport. Afterwards the board is in the store and the row mounts in the SAME frame
 * the view changes. Measured on the deployed build with a rAF trajectory recorder armed on the tab
 * press: `pinned.top` **638 → 693, a 63.0px drop**, identical at 320×568 and 390×844 and in both
 * themes, and steady on the first visit — while all three podium columns rose as ONE (their
 * photo-to-plinth gaps held at 90/74/70px across 133 frames each). At 320 the row spends that time
 * ON the podium, covering the winner's name. `probe-entry.cjs VISIT=2 SHOTS=<dir>`.
 *
 * The row therefore waits for its own containing block to stop being one, asked of the SCROLLER
 * rather than of a duration: under `prefers-reduced-motion` there is no animation, no transform
 * and no wait. Bounded, because withholding the row for ever would be a worse failure than the
 * snap — if the transform somehow never clears, the row appears anyway.
 */
/**
 * The ceiling on the wait. `.view-enter` is 220ms, so this is generous by more than double; it
 * exists because withholding the row FOR EVER would be a worse failure than the snap it replaces.
 * ⚠️ **A FRAME COUNT WAS THE FIRST SHAPE OF THIS AND IT WAS UNTESTABLE**: happy-dom's rAF fires as
 * fast as a timer, so forty "frames" were spent before the assertion could run and the row
 * appeared anyway — the test read as a broken fix. Wall-clock is what the bound actually means.
 */
const SETTLE_TIMEOUT_MS = 500;

function useContainingBlockSettled(rootRef: RefObject<HTMLElement | null>): boolean {
  const [settled, setSettled] = useState(false);
  useEffect(() => {
    const scroller = rootRef.current?.closest<HTMLElement>("[data-scroll-root]");
    if (!scroller) {
      setSettled(true);
      return;
    }
    let raf = 0;
    const startedAt = performance.now();
    const check = (): void => {
      if (
        getComputedStyle(scroller).transform === "none" ||
        performance.now() - startedAt > SETTLE_TIMEOUT_MS
      ) {
        setSettled(true);
        return;
      }
      raf = requestAnimationFrame(check);
    };
    check();
    return () => cancelAnimationFrame(raf);
  }, [rootRef]);
  return settled;
}

export function Leaderboard(): ReactNode {
  const lobbyGames = useSession((s) => s.lobbyGames);
  const meId = useSession((s) => s.profile?.id ?? null);
  const data = useSession((s) => s.leaderboard);
  const loading = useSession((s) => s.leaderboardLoading);
  const failed = useSession((s) => s.leaderboardError !== null);
  const load = useSession((s) => s.loadLeaderboard);
  const openProfile = useSession((s) => s.openProfileFromLeaderboard);
  const takeReturn = useSession((s) => s.takeLeaderboardReturn);

  /*
   * ── COMING BACK FROM SOMEBODY'S PROFILE PUTS YOU BACK ────────────────────────────────────────
   * The shell re-mounts every screen on a view change, so the filter (local state) and the scroll
   * offset (the scroller is that same re-mounted node) are both gone by the time Back lands here.
   * The position is therefore saved at the moment of leaving and read back ONCE, in a lazy
   * `useState` initialiser so the very first render already carries the right filter — seeding it
   * in an effect would fire a «هفته»/«همه» request first and repaint.
   */
  const [restored] = useState(() => takeReturn());
  const [period, setPeriod] = useState<LeaderboardPeriod>(restored?.period ?? "week");
  const [game, setGame] = useState<GameId | typeof LEADERBOARD_ALL_GAMES>(
    restored?.game ?? LEADERBOARD_ALL_GAMES,
  );
  const rootRef = useRef<HTMLDivElement | null>(null);

  /** The scroller, and the only thing on this screen that knows where it is. */
  const scroller = (): HTMLElement | null =>
    rootRef.current?.closest<HTMLElement>("[data-scroll-root]") ?? null;

  const openRow = (userId: string): void =>
    openProfile(userId, { period, game, scrollTop: scroller()?.scrollTop ?? 0 });

  /*
   * The filter list is DERIVED from the server's activation list, never a hand-written list: a
   * COMING_SOON game must appear on its own the day it is switched on, and a literal list is one
   * more place to forget the same thing. Its order is the lobby's — see `leaderboardFilterGames`.
   */
  const filterGames = useMemo(() => leaderboardFilterGames(lobbyGames), [lobbyGames]);

  useEffect(() => {
    void load(period, game);
  }, [load, period, game]);

  /*
   * Restore the offset AFTER the board has painted — at mount the list is empty, so the scroller
   * is one viewport tall and any `scrollTop` is clamped to 0. `rows.length` is the gate and the
   * `rAF` is what puts the write after layout; the ref makes it fire exactly once, so a later
   * refresh (returning to the tab, a filter change) can never yank a reading player back.
   */
  const rowCount = data?.rows.length ?? 0;
  const pendingScroll = useRef(restored?.scrollTop ?? 0);
  useEffect(() => {
    if (pendingScroll.current <= 0 || rowCount === 0) return;
    const top = pendingScroll.current;
    pendingScroll.current = 0;
    const raf = requestAnimationFrame(() => {
      const el = scroller();
      if (el) el.scrollTop = top;
    });
    return () => cancelAnimationFrame(raf);
    // `scroller` and `pendingScroll` both read refs, never state — `rowCount` is the whole trigger.
  }, [rowCount]);

  /* Returning to the tab re-reads — the cheap half of "no polling". `quiet` keeps the board on
   * screen while it refreshes, so coming back never flashes a spinner. */
  const filter = useRef({ period, game });
  filter.current = { period, game };
  useEffect(() => {
    const onVisible = (): void => {
      if (document.visibilityState !== "visible") return;
      void load(filter.current.period, filter.current.game, { quiet: true });
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [load]);

  const rows = data?.rows ?? [];
  /*
   * THE BOARD'S IDENTITY IS THE FILTER THE DATA CAME BACK FOR — NEVER THE ONE THE CHIPS ARE ON.
   *
   * This string is the `key` below, so it decides when the subtree REMOUNTS and therefore when
   * `.lb-fade` and the podium's `.lb-rise` replay. Keyed on `period`/`game` it changed the instant
   * a chip was tapped — but `load()` deliberately KEEPS the previous board on screen while the new
   * one is in flight, so the entrance animation played over the OUTGOING rows and the incoming
   * ones then swapped in silently, with no transition at all. The fade announced an arrival that
   * had not happened, and the real arrival was unannounced.
   *
   * Measured on the deployed build, relative to the tap (`probe-swapgap.cjs`): the art is a local
   * background swap and lands in the next frame at **+9 ms**, while the rows wait a round trip and
   * landed at **+41 ms** — 32 ms apart on a LAN against a 211-row staging, and the gap is the
   * round trip, so on a phone it is whatever the network costs. That window is what read as a
   * stutter: new art over an old list, then a jump.
   *
   * The response carries the filter it was built for, so keying on THAT makes the remount coincide
   * with the data. Tapping a chip now changes the art at once (the tap is acknowledged), holds the
   * previous board steady and unfaded, and plays the entrance on the rows it was written for.
   *
   * ⚠️ NOT a skeleton or a spinner in that window: it is tens of milliseconds on a good connection,
   * and a placeholder that appears and vanishes inside it IS the hitch. This screen has already had
   * one «it looks like it is loading» defect fixed for exactly that reason — see the note below.
   *
   * ⚠️ AND THE FALLBACK IS THE CHIPS, NOT A CONSTANT. Written `data ? … : "pending"` the key moves
   * on the FIRST load too — null to «week:all» — which remounts the board the moment the opening
   * read lands. That is a different screen's behaviour than the one reported and it was not asked
   * for; it also throws away the empty board a caller may already be holding. Falling back to the
   * chips means the opening read keeps the key it mounted with and nothing remounts, while a
   * filter change still cannot move it until the DATA agrees.
   */
  const boardKey = data ? `${data.period}:${data.game}` : `${period}:${game}`;
  /*
   * VARIANT B — THE ART WAITS FOR THE DATA AND THEY ARRIVE TOGETHER.
   *
   * `ArtBand` is fed this instead of the chips' `game` (it is the ONLY thing either `Podium` or
   * `EmptyBoard` uses their `game` prop for). Keyed on the chips the art swapped in the next frame
   * and the rows followed a round trip later; keyed on the data the whole board — picture and
   * list — changes in one commit.
   *
   * The tap is still answered instantly: the chip takes its own selected state on the press, which
   * is the acknowledgement the art was incidentally providing.
   */
  const artGame = data?.game ?? game;
  const podium = rows.slice(0, 3);
  const rest = rows.slice(3);
  const settled = useContainingBlockSettled(rootRef);

  return (
    <div ref={rootRef} className="flex min-h-full flex-col bg-background text-on-surface" dir="rtl">
      {/* No visible title band: the active bottom tab already names this screen, and at 320×568
          the heading cost ~110px that the list needs. The heading survives for assistive tech. */}
      <h1 className="sr-only">{fa.leaderboard.title}</h1>

      <div className="mx-auto flex w-full max-w-container-max flex-1 flex-col px-gutter pt-sm">
        <PeriodTabs value={period} onSelect={setPeriod} />
        <GameFilterGrid games={filterGames} value={game} onSelect={setGame} />

        {failed ? (
          <Notice text={fa.leaderboard.loadFailed} />
        ) : rows.length === 0 && !loading ? (
          <EmptyBoard game={artGame} period={period} />
        ) : (
          /*
           * The board resolves when the DATA changes — see `boardKey` above; the rows do not jump.
           *
           * ⚠️ **THE FADE IS ON THE LIST, NOT ON THIS WRAPPER, AND THAT IS THE WHOLE «IT LOOKS
           * LIKE IT IS LOADING» BUG.** `.lb-fade` is `opacity: 0 → 1`; on the wrapper it is an
           * ANCESTOR of the art band, and an ancestor's opacity MULTIPLIES everything beneath it
           * — so on every filter change the band restarted from the page colour and climbed back,
           * which is indistinguishable from a background being fetched. The crossfade itself was
           * fine and was simply invisible underneath it.
           *
           * Measured on the deployed build with a screencast, in the band's own left bleed where
           * nothing but art is ever drawn: on a PERIOD change — where the art does not change at
           * all, only ONE layer ever exists and its own opacity reads 0.500 throughout — the
           * painted band still fell to **25% of its resting strength**, the ancestor product
           * running 0.123 → 0.347 → 0.545 → 0.717 → 0.856 → 1.000 over 220ms. `probe-swap.cjs`.
           *
           * The podium needs nothing here: `.lb-rise` already carries `opacity: 0 → 1` of its own,
           * and the CSS beside it has always said the rising is the podium's job and the resolving
           * is the board's.
           */
          <div key={boardKey} className="flex flex-col">
            <Podium rows={podium} game={artGame} onOpen={openRow} />
            <ol className="lb-fade lb-list m-0 flex list-none flex-col p-0">
              {rest.map((row) => (
                <ListRow
                  key={row.userId}
                  row={row}
                  isMe={row.userId === meId}
                  onOpen={() => openRow(row.userId)}
                />
              ))}
            </ol>
          </div>
        )}

        {/* Under the board whenever there IS a board (rows or the empty state) — never under the
            load-failed notice, and never before the first read lands. */}
        {!failed && data ? <BoardRules /> : null}

        {/* Clears the pinned row, so the LAST row is never stranded underneath it — and it clears
            the WHOLE fixed block, fade included, not just its opaque card. Measured at 94px on the
            deployed build; the extra 6px keeps the last row off the fade's first pixel. */}
        <div aria-hidden className="h-[100px] shrink-0" />
      </div>

      {data?.me && settled ? <PinnedRow me={data.me} period={period} /> : null}
    </div>
  );
}

/**
 * The height the band's container must reserve whatever is inside it — the podium's OWN, measured
 * on the deployed build: `[data-testid=lb-podium]` is 194px at the base breakpoint and 210px from
 * 360, which puts the band at 304/320px either way.
 *
 * ⚠️ **IT IS A LITERAL BECAUSE TAILWIND SCANS SOURCE TEXT**, and it is shared because there are now
 * two containers a band can hang off and a band of the wrong height is not a smaller band — its
 * mask never reaches the `#000 172px` stop, so the art is at half strength everywhere.
 */
const STAGE_MIN_H = "min-h-[194px] min-[360px]:min-h-[210px]";

function Notice({ text }: { text: string }): ReactNode {
  return <p className="px-gutter py-lg text-center text-body-md text-on-surface-variant">{text}</p>;
}

/**
 * THE RULES CAPTION — what the board counts, under the board (operator, 2026-09-27).
 *
 * A profile's win total and its board number differ for two reasons, and nothing else on the
 * screen says either: solo wins were never counted, and since the anti-farming guards a win can be
 * refused (a 4th over the same person in one Tehran day, or a private table with a bot). The
 * operator's two sentences, one per line — TWO elements, so each is its own line at every width.
 *
 * It sits in the page's flow, above the spacer that clears the pinned row. Muted, like every
 * secondary line here: nothing on this screen but first place and the selected chip is gold.
 *
 * ⚠️ `pb-md` IS LOAD-BEARING — THE SPACER BELOW IS 100px AND THE PINNED ROW IS NOT ALWAYS SHORTER.
 * An unranked viewer's pinned row carries the cross-period hint, which wraps to a second line at
 * ≤384px: measured on the deployed build at 103px against the ranked row's 88px, so with no
 * clearance of its own the caption's last line sat 3px into the row's fade at the end of the
 * scroll. The caption brings its own 16px rather than re-tuning the spacer every other surface
 * shares. `probe-rules.cjs` measures it at four sizes, both themes, against that tallest row.
 *
 * ⚠️ `m-0` IS LOAD-BEARING — Tailwind's preflight is OFF here, so a bare `<p>` keeps the browser's
 * 1em margins and the two lines would read as two paragraphs a line apart.
 */
function BoardRules(): ReactNode {
  return (
    <div
      data-testid="lb-rules"
      className="flex flex-col gap-xs pb-md pt-md text-center text-caption text-on-surface-variant"
    >
      {fa.leaderboard.rules.map((line) => (
        <p key={line} className="m-0">
          {line}
        </p>
      ))}
    </div>
  );
}

/**
 * The board with no rows — a real state, not an error, and it still belongs to a GAME.
 *
 * ⚠️ **IT USED TO LOSE THE BAND ENTIRELY, WHICH IS WHY «منچ SHOWS NO BACKGROUND IMAGE» WAS A
 * SEPARATE BUG FROM «منچ SHOWS NO DATA».** The band hangs off the PODIUM ({@link ArtBand} explains
 * why its foot is anchored there), and `Podium` returns `null` for an empty `rows` — so a board
 * with nobody on it dropped to a bare sentence on the page colour and the screen changed shape on
 * exactly the filter a player is most likely to be puzzled by. Two things had to be true for that
 * to show up at all: a game with no wins in the window, AND a band that cannot survive one.
 *
 * ⚠️ **THE HEIGHT IS THE PODIUM'S OWN, MEASURED, AND IT IS THE POINT OF THE BOX.** `.lb-art` is
 * `top: -110px; bottom: 0` of its container, so with nothing to anchor to there is no band; and a
 * container merely as tall as the sentence would give a ~170px band, whose mask never reaches the
 * `#000 172px` stop — the art would be at half strength everywhere and read as a different screen.
 * 194px at the base breakpoint and 210px from 360 are what `[data-testid=lb-podium]` measures on
 * the deployed build, which puts the band at 304/320px either way. `probe-band.cjs` compares the
 * two live rather than trusting these numbers to stay true.
 *
 * ⚠️ **`relative z-[1]` ON THE SENTENCE IS LOAD-BEARING.** The band is a POSITIONED box at
 * `z-index: 0` and the sentence is in-flow text, which CSS paints EARLIER — so without its own
 * stacking the art paints over the words. `.lb-stage > *` does the same job for the columns.
 */
function EmptyBoard({
  game,
  period,
}: {
  game: GameId | typeof LEADERBOARD_ALL_GAMES;
  period: LeaderboardPeriod;
}): ReactNode {
  return (
    <div className={`relative shrink-0 ${STAGE_MIN_H}`} data-testid="lb-empty">
      <ArtBand game={game} />
      <div className="relative z-[1]">
        <Notice text={fa.leaderboard.empty[period]} />
      </div>
    </div>
  );
}

/**
 * The three period tabs, in the app's segmented-control idiom.
 *
 * ⚠️ `border-solid` IS LOAD-BEARING — Tailwind's preflight is OFF here, so a `border` utility sets
 * a WIDTH and nothing sets `border-style`; the browser falls back to `none` and no line renders.
 */
function PeriodTabs({
  value,
  onSelect,
}: {
  value: LeaderboardPeriod;
  onSelect: (p: LeaderboardPeriod) => void;
}): ReactNode {
  return (
    <div
      role="tablist"
      aria-label={fa.leaderboard.title}
      /* ⚠️ `relative z-[2]` IS LOAD-BEARING, NOT TIDINESS. The art band reaches up to this row's
         own top edge (`.lb-art`, -119px) and is a POSITIONED box at `z-index: 0`, which paints
         over the backgrounds of in-flow siblings that come before it in the tree. Without a
         stacking context of its own this control's track is painted on by the picture behind it.
         The filter strip's wrapper below has carried the same pair since the band was built. */
      className="lb-seg relative z-[2] flex w-full shrink-0 gap-1 rounded-xl border border-solid border-outline-variant/60 p-[3px]"
    >
      {LEADERBOARD_PERIODS.map((p) => {
        const active = p === value;
        return (
          <button
            key={p}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onSelect(p)}
            /* ⚠️ `duration-75`, NOT `duration-200` — THIS TRANSITION IS THE WHOLE ACKNOWLEDGEMENT
               OF THE TAP. Since the art waits for the data ({@link artGame}), the chip taking its
               selected state is the ONLY thing that answers a press before the round trip returns.
               At 200ms it did not: measured on the deployed build, the tapped chip's background ran
               `38,43,41` at rest → `38,43,41` at +22ms (the state HAD flipped) → `41,45,41` at
               +34ms → `52,52,43` at +50ms, reaching its gold `199,154,63` only at +206ms. Three
               points of red against the ground it sits on is not a visible answer. 75 is the scale
               step nearest the 80ms asked for; an arbitrary `duration-[80ms]` is one more class
               that can silently compile to nothing. */
            className={`flex-1 cursor-pointer appearance-none rounded-lg border-solid px-2 py-[6px] font-sans text-body-md transition-colors duration-75 active:scale-[0.97] ${
              active
                ? "lb-token font-bold"
                : "border border-transparent bg-transparent text-on-surface-variant"
            }`}
          >
            {PERIOD_LABEL[p]}
          </button>
        );
      })}
    </div>
  );
}

/**
 * The game filter — EXACTLY THREE ROWS for the nine games (card-86 round 2, the operator: «all 9
 * games in exactly 3 rows, never 4»): «همه» beside the first three card games, then the fourth card
 * game and the board games three a row, all in the lobby's order ({@link filterRows}).
 *
 * ⚠️ **EACH ROW'S CHIPS SHARE IT EVENLY, AND THE TWELVE TRACKS ARE WHY.** A row of four spans three
 * tracks a chip, a row of three four, a short last row of two six, of one all twelve — every row's
 * spans add up to exactly twelve, so auto-placement can never pull a chip up into a row with room
 * left over, and no viewport or text zoom can wrap a chip into a fourth row: the tracks shrink, the
 * row count does not.
 *
 * ⚠️ **EVERY GAME IS ON SCREEN — THE STRIP HAS NO HEIGHT OF ITS OWN AND NO INNER SCROLL.** It used
 * to reserve two rows (64px) and scroll a third inside that box, so the tenth cell made a row the
 * player had to find by scrolling a filter strip: staging showed تخته نرد and دوز cut off below
 * «مار پله» (measured 2026-11-03: the box 64px, its content 98px). The strip now takes the height
 * of its rows — three of them, 3 × 30px + 2 × 4px gaps = 98px — so a game an admin switches on or
 * off can move the page by a row, which is the price of never hiding one.
 */
function GameFilterGrid({
  games,
  value,
  onSelect,
}: {
  games: readonly GameId[];
  value: GameId | typeof LEADERBOARD_ALL_GAMES;
  onSelect: (g: GameId | typeof LEADERBOARD_ALL_GAMES) => void;
}): ReactNode {
  /* Each chip with its span: the row's twelve tracks shared by the row's chips. A row of four AFTER the
     first (a tenth game, card-60, D24) takes all twelve tracks as one cell, and its four chips share it
     by their CONTENTS (`.lb-chip-row`): an even quarter cut «هفت خبیث» at 240 px and at ×1.5, where the same
     row's «منچ» has room to give (measured on `preview-leaderboard.html`). Every other row is as it was. */
  const rows = filterRows(games);
  const chip = (g: GameId | typeof LEADERBOARD_ALL_GAMES, span: number | null): ReactNode => {
    const active = g === value;
    return (
      <button
        key={g}
        type="button"
        aria-pressed={active}
        onClick={() => onSelect(g)}
        style={span === null ? undefined : { gridColumn: `auto / span ${span}` }}
        className={`flex min-w-0 cursor-pointer appearance-none items-center justify-center rounded-lg px-0 py-[6px] font-sans text-label-md transition-colors duration-75 active:scale-[0.97] ${
          active ? "lb-token font-bold" : "lb-chip text-on-surface-variant"
        }`}
      >
        {/* ⚠️ **THE MARK IS ITS OWN FLEX ITEM, NOT A CHARACTER IN THE LABEL** — the same
                  shape the podium's medal uses, and for the same two reasons. Flex items sit in
                  separate inline formatting contexts, so UAX#9 never sees an emoji and a Persian
                  word in one run and the bidi question this repo insists on MEASURING cannot
                  arise; and `truncate` moves onto the LABEL, so a long name shortens without ever
                  eating the mark that identifies the cell. */}
        {/* ⚠️ «همه» IS THE ONE CELL WITH NO MARK, AND IT MUST NOT BORROW THE FALLBACK.
                  `gameMeta` answers an unknown id with 🎴, which on this strip would be a tenth
                  glyph meaning "a card game" on the cell that is the ABSENCE of a filter. It is
                  not a game and has no mark to draw from; the branch is the same one the label
                  already takes. */}
        {g === LEADERBOARD_ALL_GAMES ? null : (
          <span aria-hidden className="lb-chip-mark shrink-0 text-[12px] leading-none">
            <GameMark icon={gameMeta(g).icon} />
          </span>
        )}
        {/* ⚠️ **THE LABEL DROPS TO 12px AT THE BASE BREAKPOINT, AND THAT IS ARITHMETIC, NOT
                  TASTE.** Measured on the deployed build when the strip was FOUR cells a row: a
                  cell at 320 was 69px wide with 59px of content at `px-1`, «تخته نرد» — the
                  longest label there is — measures 47.8px at `text-label-md`, and a 12px mark plus
                  its gap is 17.3. That was 6px more than existed, and a truncated FILTER label is a
                  worse thing than a small one. At 12px the pair is 58.6. Only the SIZE moves —
                  weight, tracking and the 16px line box come from the button, so the cell stays
                  exactly 30px tall.
                  ⚠️ **AND THE CHIP HAS NO SIDE PADDING — THE FIRST ROW'S FOUR ARE WHY.** Four a
                  row (card-86 round 2) a chip is 49px at the 240px-wide zoomed screen and 79 at
                  360 under ×1.5 text zoom, and measured on the dev harness
                  (`preview-leaderboard.html`) «بی‌دل» came out 1px and 2px short of the room
                  `px-[2px]` / `px-1` left it, so it truncated. The content is CENTRED, so the
                  padding never showed on a chip whose content fits; dropping it only gives the
                  tight chips back the pixels they were missing. */}
        <span className="min-w-0 truncate text-[12px] min-[360px]:text-[14px]">
          {g === LEADERBOARD_ALL_GAMES ? fa.leaderboard.allGames : gameMeta(g).fa}
        </span>
      </button>
    );
  };

  return (
    /* ⚠️ **THERE IS NO BACKING PLATE ANY MORE, AND ITS REMOVAL IS THE FIX FOR «CUT WITH A
       KNIFE».** This wrapper used to draw one — `rgb(var(--c-background) / 0.82)`, inset -6/-10,
       radius 14 — so that seven dark tokens would not float on a picture. It also gave the art a
       hard rectangular boundary: measured as the art's own contribution, the band stepped **76% of
       its entire peak in ONE pixel** at that slab's bottom edge (69% on light), while outside the
       slab's horizontal reach there was no step at all. `.lb-art-layer`'s mask holds the art to
       ~7% up here instead, which buys the same legibility with no edge to see. `probe-edge.cjs`.

       ⚠️ `relative z-[2]` STAYS. The band reaches above this row, and a positioned box at
       `z-index: 0` paints over in-flow siblings' backgrounds — the chips need to sit ON the art.
       So does the `mt-1`, which the grid used to carry, so nothing moves. */
    <div className="relative z-[2] mt-1 shrink-0">
      <div
        data-testid="lb-filter-grid"
        className="lb-filter-strip"
        role="group"
        aria-label={fa.leaderboard.title}
      >
        <div className="grid grid-cols-12 gap-1">
          {rows.map((row, i) =>
            i > 0 && row.length === 4 ? (
              <div
                key={`row-${i}`}
                data-chip-row=""
                className="lb-chip-row"
                style={{ gridColumn: `auto / span ${FILTER_TRACKS}` }}
              >
                {row.map((g) => chip(g, null))}
              </div>
            ) : (
              row.map((g) => chip(g, FILTER_TRACKS / row.length))
            ),
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Medal treatment per PODIUM SLOT — not per rank.
 *
 * ⚠️ **THE STEP IS THE POSITION; THE NUMERAL IS THE RANK. THEY ARE DIFFERENT QUESTIONS.** This was
 * keyed on the rank so that equal ranks would look equal — and on a board where ties are ordinary
 * that erased the podium: staging's own top three all had four wins, so `medalFor(rank)` painted
 * three identical gold columns of identical height. A podium's blocks are 1-2-3 whoever stands on
 * them; two players tied for first get gold and silver and BOTH print «۱», which is what a real
 * podium does and what the board's own ordering already says.
 *
 * The three metals separate by HUE **and** by luminance, in opposite directions per theme. On dark
 * they descend (gold is the brightest thing on the page); on light they ascend, because against a
 * near-white background weight comes from saturation — see the metal block in `theme.css`, which
 * holds the per-theme token aliases and the contrast constraint on each gradient.
 */
interface Medal {
  /** Plinth height. Responsive: the 320×568 budget is what sets the base. */
  step: string;
  metal: string;
  /** The 🥇/🥈/🥉 worn beside the name — see {@link Podium}. */
  badge: string;
  name: string;
  numeral: string;
  /** The win line ON the plinth. Size only: the colour is the metal's own ink. */
  wins: string;
  avatar: number;
  ring: string;
}

/*
 * ⚠️ **THE HEIGHTS MOVED BECAUSE THE WIN COUNT MOVED ONTO THE BLOCK** — 72/52/38 → 76/56/44 at
 * the base. The plinth now holds two lines instead of one, and the shortest of the three is what
 * sets the floor: a 22px numeral at `leading-none` + a 2px gap + an 11px caption is 36px of
 * content inside a 44px block, and «برد» wants its descender room, so the caption is NOT
 * `leading-none`. The COLUMN is shorter overall despite that — the line that used to sit above
 * each block cost ~26px of the 320×568 budget and buys 6px of block back.
 */
const MEDAL: readonly Medal[] = [
  {
    step: "h-[76px] min-[360px]:h-[88px]",
    metal: "lb-step--gold",
    badge: "🥇",
    name: "font-bold text-on-surface",
    numeral: "text-display-lg leading-none",
    wins: "text-caption leading-[1.25]",
    avatar: 62,
    ring: "lb-crown border-secondary [[data-theme=light]_&]:border-secondary-container",
  },
  {
    step: "h-[56px] min-[360px]:h-[66px]",
    metal: "lb-step--silver",
    badge: "🥈",
    name: "text-on-surface-variant",
    numeral: "text-headline-md leading-none",
    wins: "text-[11px] leading-[1.2]",
    avatar: 46,
    ring: "border-outline",
  },
  {
    step: "h-[44px] min-[360px]:h-[52px]",
    metal: "lb-step--bronze",
    badge: "🥉",
    name: "text-on-surface-variant",
    numeral: "text-headline-md leading-none",
    wins: "text-[11px] leading-[1.2]",
    avatar: 42,
    ring: "border-tertiary",
  },
];

/** A podium never has more than three slots; a shorter board simply renders fewer. */
function medalFor(slot: number): Medal {
  return MEDAL[Math.min(slot, MEDAL.length - 1)]!;
}

/**
 * ⚠️ **THE ART IS BAKED FOR A PAGE COLOUR, SO IT MUST FOLLOW THE ATTRIBUTE THAT DECIDES THAT
 * COLOUR — `<html data-theme>` — AND NOT `getResolvedTheme()`.** The two normally agree, because
 * `applyThemePref()` writes the attribute from that same value; they come apart the moment anything
 * stamps the attribute directly, which every browser probe in this repo does and which would leave
 * dark-baked art sitting on a light page. Reading the attribute also means the art and the CSS can
 * never disagree about which theme is on screen, whatever route put it there.
 *
 * `onThemeChange` is subscribed BESIDE the observer rather than instead of it: the observer is what
 * makes this correct, and the listener is what makes it prompt (a MutationObserver delivers on a
 * microtask, and a theme flip should not cost the band a frame).
 */
function readArtTheme(): ResolvedTheme {
  return document.documentElement.dataset["theme"] === "light" ? "light" : "dark";
}

function subscribeArtTheme(onChange: () => void): () => void {
  const stop = onThemeChange(onChange);
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["data-theme"],
  });
  return () => {
    stop();
    observer.disconnect();
  };
}

/**
 * ── THE BAND BEHIND THE PODIUM ────────────────────────────────────────────────────────────────
 *
 * The selected game's own artwork, running from above the period tabs down to the foot of the
 * podium and DISSOLVING at both ends rather than stopping at an edge.
 *
 * ⚠️ **ITS FOOT IS THE PODIUM'S FOOT, WHICH IS WHY IT IS ANCHORED HERE AND NOT ON THE SCREEN.**
 * The podium's height is the part that moves — 76/56/44px plinths at the base breakpoint and
 * 88/66/52 above, and a two-column board is shorter again — so a band positioned from the top with
 * a height in pixels would have to restate all of that and would drift the first time a plinth
 * changed. Anchored to the stage, `bottom: 0` IS the list's top at every size, for free. What
 * reaches UPWARD is the constant (`.lb-art`'s -110px).
 *
 * ⚠️ **THE LIST STAYS OPAQUE.** Operator's call, 2026-09-11: text contrast is worth more than the
 * art. Measured before asking — with a translucent slab the art reaches 55% of the screen at rest
 * and 65% scrolled, against 23% and 6% with an opaque one.
 *
 * ⚠️ **THE SWAP IS INSTANT, AND THAT IS A REVERSAL.** It crossfaded for two days: two layers, the
 * incoming one animating in on top of the outgoing one. Operator's call, 2026-09-11 — «the
 * incoming image seems to lag, and parts of the outgoing one are still visible underneath it; a
 * clean instant swap is better than a transition that reads as a glitch». Both halves of that are
 * inherent to the shape rather than to its timing: an image only paints when it has DECODED, and
 * two stacked pictures at partial alpha ARE both visible by construction. So there is one layer
 * now, and nothing here to tune. ⚠️ It leaves `previousGame` with no reader — do not reintroduce a
 * ref above the board's key to feed it; the board is keyed on the filter precisely so everything
 * beneath it is rebuilt, and the crossfade was the only thing that ever needed to see across that.
 *
 * ⚠️ **«همه» HAS NO GAME ART AND STILL GETS A BAND** — 🏆 on the app's own field green, softened
 * the same way (`tools/bg-art/make-trophy.cjs`). An empty band would make the screen change shape
 * on the one filter every player starts on, and it first shipped carrying `logo.jpg`: the Bing
 * roundel and the wordmark, i.e. a BRAND MARK on the one state in ten where the other nine carry a
 * photograph of the thing the board is about.
 */
function ArtBand({ game }: { game: GameId | typeof LEADERBOARD_ALL_GAMES }): ReactNode {
  /* ⚠️ THE ART IS BAKED FOR A PAGE COLOUR, SO IT FOLLOWS `<html data-theme>` — see
     {@link readArtTheme} for why that and not `getResolvedTheme()`. */
  const theme = useSyncExternalStore(subscribeArtTheme, readArtTheme, () => "dark" as const);
  const jpg = GAME_ART_BLUR[theme][game];
  // «مار پله»'s WebP twin, when the art tools shipped one (card-86 round 2): the JPEG stays the first
  // declaration, the WebP an `image-set` over it (`styles/ladders-art.css`). Absent → as before.
  const webp = GAME_ART_BLUR_WEBP[theme][game];
  return (
    <div className="lb-art" aria-hidden data-testid="lb-art">
      {webp ? (
        <div
          data-art-layer
          className="lb-art-layer ladders-art-band"
          style={
            {
              "--ladders-art-jpg": `url(${jpg})`,
              "--ladders-art-webp": `url(${webp})`,
            } as CSSProperties
          }
        />
      ) : (
        <div data-art-layer className="lb-art-layer" style={{ backgroundImage: `url(${jpg})` }} />
      )}
    </div>
  );
}

/**
 * The top three, as stepped columns.
 *
 * ⚠️ **PAINTED LEFT-TO-RIGHT AS 2nd · 1st · 3rd — SECOND ON THE LEFT OF FIRST.** Operator's call,
 * 2026-09-11. The DOM order is [1st, 2nd, 3rd] and the visual order is set by `order`, because a
 * grid under this app's `dir="rtl"` root lays its column tracks RIGHT-to-left — so DOM order and
 * paint order are mirror images, and writing the columns in visual order would put them backwards
 * (the trap that mirrored دوز's whole board). Reading order stays 1-2-3; only the paint moves.
 *
 * ⚠️ **THE `order` VALUES READ RIGHT-TO-LEFT ARE `1,2,3` UNDER EITHER ARRANGEMENT**, so a probe
 * that checks only that sequence is green for both and cannot see this swap at all. What
 * discriminates is WHICH ROW lands where: right-to-left the DOM slots must be 3rd · 1st · 2nd.
 *
 * The blocks meet on one baseline with a 3px seam and no bottom radius, so they read as a single
 * plinth standing on the list rather than as three cards; the win count is ON the block, which
 * both saves the line the tall first step spends and gives the metal something to say.
 */
function Podium({
  rows,
  game,
  onOpen,
}: {
  rows: readonly LeaderboardRow[];
  game: GameId | typeof LEADERBOARD_ALL_GAMES;
  onOpen: (userId: string) => void;
}): ReactNode {
  /*
   * ⚠️ **NO EARLY RETURN ON AN EMPTY `rows` — THAT RETURN WAS «IT LOOKS LIKE IT'S LOADING», TWICE.**
   * The band hangs off this stage, so `return null` took the art with it, and `rows` is empty in
   * TWO states, not one: the empty board, and — every filter change — the ~430ms while the new
   * board is being fetched, because a non-quiet load clears the rows and `loading` sends the screen
   * down this branch rather than to {@link EmptyBoard}. Measured on the deployed build with a
   * screencast: the band fell to **34% of its resting strength for 432ms** on «همه» → «حکم», with
   * ONE layer at opacity 1.000 and an ancestor product of 1.000 throughout — no fade anywhere, the
   * box simply was not there. The first fix for this ancestor-opacity family (`.lb-fade` off the
   * keyed wrapper) was correct and was only half of it.
   *
   * The stage costs nothing when it is full: `min-h` is the height the columns already have.
   */
  const ORDER = [2, 3, 1]; // rows[0] centre, rows[1] to its LEFT, rows[2] to its right (RTL)
  return (
    <div
      className={`lb-stage relative grid grid-cols-3 items-end gap-[3px] pt-6 ${STAGE_MIN_H} min-[360px]:pt-7`}
      data-testid="lb-podium"
    >
      <ArtBand game={game} />
      {rows.map((row, i) => {
        const medal = medalFor(i);
        return (
          <div
            key={row.userId}
            data-col
            /* ⚠️ **THE COLUMNS ARE MARKED, BECAUSE THE PODIUM IS NO LONGER ONLY COLUMNS.** The art
               band is a child of this stage too, so every reader that took `podium.children` to
               mean "the three columns" silently gained a fourth — three unit tests and five probes
               did, and two of them failed in a way that read as a podium bug. Select `[data-col]`. */
            className="lb-rise relative flex min-w-0 flex-col items-center"
            style={{ order: ORDER[i], animationDelay: `${i * 80}ms` }}
          >
            {/* ⚠️ THE BUTTON WRAPS THE PHOTO AND THE NAME — NOT THE PLINTH. A tap target that
                covered the whole column would make the block look pressable and give the player no
                edge to read; this is the same shape the friends list has always had, through the
                same `ProfileTap`. */}
            <ProfileTap
              user={row}
              onOpen={() => onOpen(row.userId)}
              className="flex w-full min-w-0 flex-col items-center"
            >
              <Avatar
                avatarId={row.avatarId}
                avatarPhotoId={row.avatarPhotoId}
                size={medal.avatar}
                ringClass={`${medal.ring} rounded-full`}
                ringWidth={2}
              />
              {/* ⚠️ THE MEDAL IS A SIBLING OF THE NAME, NOT INSIDE IT. The name truncates, and a
                  medal inside that span is the FIRST thing a long Persian name would eat — the
                  one row on the podium guaranteed to have a long name is the one the seed puts
                  there on purpose. `min-w-0` on the column AND on the name, because a grid item's
                  default `min-width:auto` lets a long name push the track wider than its third. */}
              <span className="mt-1 flex w-full min-w-0 items-center justify-center gap-[3px] px-[2px]">
                <span aria-hidden className="shrink-0 text-[13px] leading-none">
                  {medal.badge}
                </span>
                {/* ⚠️ **`dir="auto"` — WITHOUT IT AN RTL BLOCK CLIPS THE WRONG END OF A LATIN
                    NAME.** `text-overflow: ellipsis` cuts at the line's END in the INLINE
                    direction, and inside `dir="rtl"` that end is the visual LEFT — which for an
                    LTR run is its BEGINNING. Measured on the deployed build: «Christopher
                    Abernathy-Fitzgerald» rendered as «...gerald», i.e. a player identified by the
                    one part of their name nobody knows them by. `dir="auto"` resolves each name
                    from its own first strong character, so Persian still clips on the left and
                    Latin clips on the right; it also isolates the span, so a Latin name can no
                    longer reorder the medal beside it. */}
                <span
                  dir="auto"
                  className={`min-w-0 truncate text-center text-body-md ${medal.name}`}
                >
                  {displayName(row)}
                </span>
              </span>
            </ProfileTap>
            {/* ⚠️ **THE WIN COUNT IS ON THE BLOCK.** It used to sit above it, where it was a third
                loose line between the name and the metal and the plinth carried nothing but a
                numeral. On the block the two numbers read as one statement — the place, and what
                earned it — and the metal has something to say. */}
            <div
              data-step
              data-medal={medal.metal}
              /* ⚠️ **THE 4px IS THE ONLY AIR BETWEEN A NAME AND THE METAL IT STANDS ON**, and it
                 costs exactly itself: `items-end` bottom-aligns the columns, so every column grows
                 by 4 and the list below starts 4px lower. Measured at 320×568 before asking for
                 it — the medal beside the 2nd and 3rd names was touching its plinth's top edge. */
              className={`lb-step mt-1 ${medal.metal} ${medal.step}`}
            >
              <span data-rank className={`font-sans ${medal.numeral}`}>
                {faDigits(row.rank)}
              </span>
              <span
                data-wins
                className={`max-w-full truncate px-1 text-center font-sans ${medal.wins}`}
              >
                {faDigits(row.wins)} {fa.leaderboard.wins}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * One ranked row: rank · photo · name · wins.
 *
 * ⚠️ **THE TWO NUMBERS MUST NOT READ ALIKE.** A bare rank at one end and a bare win count at the
 * other were two unlabelled numerals, and on a board where many players share one win they were
 * often the same numeral. The win count therefore carries its «برد» label — the same wording the
 * podium uses — and the rank is a muted fixed-width numeral, the SAME colour here as in the
 * pinned row so the reader learns one thing about what that column means.
 *
 * ⚠️ **NOTHING HERE IS GOLD.** The win count used to be `text-secondary` on every row, which put
 * the screen's one accent on forty-odd rows at once and left first place with nothing of its own.
 * The list is the floor the podium stands on: `on-surface` for the number, `on-surface-variant`
 * for its label, and the separators are hairlines drawn by `.lb-list`.
 */
function ListRow({
  row,
  isMe,
  onOpen,
}: {
  row: LeaderboardRow;
  isMe: boolean;
  onOpen: () => void;
}): ReactNode {
  return (
    <li className={`flex items-center gap-sm px-2 py-[9px] ${isMe ? "bg-secondary/10" : ""}`}>
      <span className="w-6 shrink-0 text-center text-body-md tabular-nums text-on-surface-variant">
        {faDigits(row.rank)}
      </span>
      {/* ⚠️ THE RANK AND THE WIN COUNT STAY OUTSIDE THE BUTTON. Wrapping the whole row would make
          every pixel of it a tap target with nothing to say it is one; the face and the name are
          what a person reaches for, and they are what the friends list has always offered. */}
      <ProfileTap user={row} onOpen={onOpen} className="flex min-w-0 flex-1 items-center gap-sm">
        <Avatar
          avatarId={row.avatarId}
          avatarPhotoId={row.avatarPhotoId}
          size={36}
          ringClass="border-outline-variant rounded-full"
          ringWidth={1}
        />
        {/* `dir="auto"` for the same reason the podium carries it — see {@link Podium}. The
            alignment is pinned to the RIGHT rather than left to `start`, because `start` follows
            the span's own resolved direction and would push a Latin name away from the face it
            belongs to, leaving a hole between them. */}
        <span
          dir="auto"
          className="min-w-0 flex-1 truncate text-right text-body-md text-on-surface"
        >
          {displayName(row)}
        </span>
      </ProfileTap>
      <span className="shrink-0 text-body-lg text-on-surface">
        {faDigits(row.wins)}{" "}
        <span className="text-caption text-on-surface-variant">{fa.leaderboard.wins}</span>
      </span>
    </li>
  );
}

/**
 * The caller's own row, pinned above the bottom nav.
 *
 * It is the same shape as a list row in the accent colour, with a second line under the name for
 * the gap to the rank above. Three things make it read as a separate layer rather than as a card
 * dropped onto the list: a full-width backing in the PAGE colour (so a scrolling row cannot show
 * through its rounded corners), a fade above that so the list dissolves into it instead of being
 * chopped, and a real gap to the nav. `bottom` clears the nav plus the device's true inset — a
 * bare `env()` reads 0 in Telegram's fullscreen, which is exactly the case that HAS an inset.
 */
function PinnedRow({
  me,
  period,
}: {
  me: NonNullable<LeaderboardResponse["me"]>;
  period: LeaderboardPeriod;
}): ReactNode {
  const profile = useSession((s) => s.profile);
  const ranked = me.rank !== null;
  /*
   * An unranked viewer's line is «you have nothing here» PLUS, when the server found one, where
   * their wins actually are. The two halves are composed rather than written out as six sentences
   * so the period-naming fix above cannot drift from the hint.
   */
  const unranked = me.elsewhere
    ? `${fa.leaderboard.unranked[period]}، ${fa.leaderboard.butElsewhere[me.elsewhere.period](faDigits(me.elsewhere.wins))}`
    : fa.leaderboard.unranked[period];
  /*
   * ⚠️ THERE IS NO «آمار تو مخفی است» ARM, AND IT MUST NOT COME BACK. A fourth branch used to sit
   * here — «آمار تو مخفی است، پس در جدول عمومی نمایش داده نمی‌شوی» — for a caller `hideStats`
   * withheld from the board. That exclusion was removed on 2026-09-14 (a player with 227 online
   * wins had reported being unranked because of it), so a ranked caller IS on the board they are
   * looking at and the sentence would simply be false. The `rankedPublicly` field it read is gone
   * from the wire entirely as of 2026-09-21 — see `### Standing verdicts` in CLAUDE.md.
   */
  const second = !ranked
    ? unranked
    : me.winsToNextRank !== null
      ? fa.leaderboard.toNextRank(faDigits(me.winsToNextRank))
      : fa.leaderboard.topOfBoard;

  return (
    <div
      data-testid="lb-pinned"
      /* `lb-fade` is OPACITY ONLY (see theme.css) — the row appears where it belongs rather than
         popping into place, and it cannot move, which is the whole point of the wait above. */
      className="lb-fade fixed inset-x-0 z-20"
      style={{
        bottom: "calc(63px + max(env(safe-area-inset-bottom), var(--tg-safe-bottom, 0px)))",
      }}
    >
      {/* The list dissolves into the backing rather than being cut by a hard edge. */}
      <div
        aria-hidden
        className="h-5 w-full bg-gradient-to-t from-background to-transparent"
        role="presentation"
      />
      <div className="w-full bg-background pb-[10px]">
        <div className="mx-auto w-full max-w-container-max px-gutter">
          <div className="flex items-center gap-sm rounded-xl border border-solid border-outline-variant bg-surface-container-high px-2 py-[9px] shadow-lg">
            {/* ⚠️ `min-w-6`, NOT `w-6` — THE PINNED ROW IS THE ONE PLACE A RANK IS UNBOUNDED.
                A list row's rank comes off a 50-row page and is two digits at most, but `me.rank`
                is `better + 1` over the WHOLE ranked population: 8,542 players today and 101,487
                users, so six digits is the real ceiling. At `w-6` (24px) a four-digit rank's ink
                measured 36.9px and ran INTO the avatar — the slot has no `overflow:hidden`, so it
                did not clip, it spilled, and the avatar paints over it because it comes later in
                the DOM. That is «the photo covers the number». A min-width grows to whatever the
                digits need and leaves rank 1 exactly as it was. `probe-rankfit.cjs`. */}
            <span className="min-w-6 shrink-0 text-center text-body-md tabular-nums text-on-surface-variant">
              {ranked ? faDigits(me.rank!) : ""}
            </span>
            {profile ? (
              <Avatar
                avatarId={profile.avatarId}
                avatarPhotoId={profile.avatarPhotoId}
                size={36}
                ringClass="border-outline rounded-full"
                ringWidth={1}
              />
            ) : null}
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-body-md font-bold text-on-surface">
                {fa.leaderboard.you}
              </span>
              {/* NOT truncated: the cross-period hint is a whole sentence and a clipped one would
                  lose exactly the half that carries the number. It wraps to a second line, which
                  the pinned row can afford — it is `fixed`, so it grows away from the list. */}
              <span className="text-caption leading-snug text-on-surface-variant">{second}</span>
            </span>
            {/* An unranked viewer is told so on the line above; a «۰ برد» beside it would say the
                same thing a second time in a weaker way. */}
            {ranked ? <CountUp value={me.wins} /> : null}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * The viewer's own win count, counting up when it CHANGES — not on first paint.
 *
 * A number that animates on arrival is noise; one that animates when it moves tells the player
 * something happened, which on this screen is the only event worth announcing. Skipped entirely
 * under `prefers-reduced-motion`, and skipped for a jump too large to read as counting.
 */
function CountUp({ value }: { value: number }): ReactNode {
  const [shown, setShown] = useState(value);
  const previous = useRef(value);

  useEffect(() => {
    const from = previous.current;
    previous.current = value;
    if (from === value) return;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
    if (reduce || Math.abs(value - from) > 40) {
      setShown(value);
      return;
    }
    let raf = 0;
    const started = performance.now();
    const DURATION = 420;
    const tick = (now: number): void => {
      const t = Math.min(1, (now - started) / DURATION);
      setShown(Math.round(from + (value - from) * t));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);

  return (
    <span data-testid="lb-my-wins" className="shrink-0 text-body-lg text-secondary">
      {faDigits(shown)}{" "}
      <span className="text-caption text-on-surface-variant">{fa.leaderboard.wins}</span>
    </span>
  );
}
